import { useContext } from 'react';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { useHerd } from 'app/agriculture/queries/herds/useHerd';
import { HerdIcon } from 'app/agriculture/icons';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { Autocomplete } from 'app/agriculture/forms/components/Autocomplete/Autocomplete';
import { useHerdUpdateMutation } from 'app/agriculture/mutations/herds/useHerdUpdateMutation';
import { ServicesContext } from 'app/items/shared/context_interface';
import { isDate, isFuture } from 'date-fns';
import { useForm, SubmitHandler, FormProvider, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { HerdPaddockDisplay } from './HerdPaddockDisplay';
import { HerdTotalAnimalsDisplay } from './HerdTotalAnimalsDisplay';
import { validateHerdTotalAnimals } from '../../utils';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import './HerdEditForm.scss';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { DatePickerInput } from 'app/agriculture/forms/components/DatePicker/DatePickerInput';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

interface HerdEditFormProps {
    herdId: string;
}

interface HerdEditFields {
    name: string;
    snapshot: {
        paddockId: string | null;
        totalAnimals: number;
        timestamp?: Date;
    };
}

export function HerdEditForm({ herdId }: HerdEditFormProps) {
    const { timeZone } = useTimeZone();
    const herd = useHerd({ herdId });
    const snapshot = useSnapshot({ snapshotType: 'Herd', parentId: herdId, shelfLife: null });
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const formController = useForm<HerdEditFields>({
        mode: 'all',
        defaultValues: {
            name: herd.name,
            snapshot: {
                paddockId: snapshot?.paddockId ?? null,
                totalAnimals: snapshot?.totalAnimals,
                timestamp: new TZDate(new Date(), timeZone),
            },
        },
        shouldFocusError: true,
    });
    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isDirty },
        reset,
    } = formController;

    const { agricultureToastService } = useContext(ServicesContext);
    const { mutate, isPending } = useHerdUpdateMutation({
        onSuccess: () => {
            reset();
            navigate('../');
        },
        onError: (error) => {
            console.error('Error creating herd', error);
            agricultureToastService
                .showToast({
                    title: 'Herd Update Failed:',
                    subtext: 'Something went wrong',
                    type: AgricultureToastType.ERROR,
                    variant: 'simple',
                })
                .then()
                .catch((e: unknown) => {
                    console.error(e);
                });
        },
    });

    const onSubmit: SubmitHandler<HerdEditFields> = (formData) => {
        if (formData.snapshot.timestamp == null) return;

        mutate({
            ...formData,
            id: herd.id,
            snapshot: {
                ...formData.snapshot,
                timestamp: formData.snapshot.timestamp.toISOString(),
            },
        });
    };

    const attachOptions = useAttachOptions(['Paddock']);
    return (
        <ContentBox title="Herd Edit" Icon={HerdIcon} variant={'md'} className="NuHerdEditForm">
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="NuHerdEditForm__fields">
                        <ContentBox.Section className="NuHerdEditForm__fields__time-static-container">
                            <Input
                                label="Herd name"
                                {...register('name', {
                                    required: {
                                        value: true,
                                        message: 'Please enter a name for your herd',
                                    },
                                })}
                                errorMessage={errors.name?.message}
                            />
                            <SimpleGrid
                                columns={2}
                                className={'NuHerdEditForm__stat-display-container'}
                            >
                                <StatDisplay
                                    id="herd-animal"
                                    name="Herd Animal"
                                    value={herd.animalSpecies}
                                />
                                {herd.herdType ? (
                                    <StatDisplay
                                        id="herd-type"
                                        name="Herd Type"
                                        value={herd.herdType}
                                    />
                                ) : null}
                            </SimpleGrid>
                        </ContentBox.Section>
                        <ContentBox.Section className="NuHerdEditForm__fields__time-dependant-container">
                            <Controller
                                control={control}
                                name="snapshot.timestamp"
                                rules={{
                                    validate: {
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                        noFutureDate: (value) => {
                                            if (value == null) {
                                                return 'Please enter a valid date';
                                            }

                                            if (isFuture(value)) {
                                                return 'Please select a past date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        {...field}
                                        label="Date"
                                        showTimeInput
                                        initialValue={formState.defaultValues?.snapshot?.timestamp}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(date);
                                        }}
                                    />
                                )}
                            />
                            <SimpleGrid columns={2}>
                                <SuspenseWrapper>
                                    <HerdPaddockDisplay
                                        herdId={herd.id}
                                        latestBefore={
                                            formController.getValues().snapshot.timestamp ?? null
                                        }
                                    />
                                </SuspenseWrapper>
                                <Autocomplete
                                    fieldName="snapshot.paddockId"
                                    options={attachOptions}
                                    noun={'To Paddock'}
                                    formController={formController}
                                />
                            </SimpleGrid>
                            <SimpleGrid columns={2}>
                                <SuspenseWrapper>
                                    <HerdTotalAnimalsDisplay
                                        herdId={herdId}
                                        latestBefore={
                                            formController.getValues().snapshot.timestamp ?? null
                                        }
                                    />
                                </SuspenseWrapper>
                                <Input
                                    label="To total animals"
                                    {...register('snapshot.totalAnimals', {
                                        validate: validateHerdTotalAnimals,
                                    })}
                                    errorMessage={errors.snapshot?.totalAnimals?.message}
                                    type="number"
                                    step={1}
                                    min={1}
                                />
                            </SimpleGrid>
                        </ContentBox.Section>
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Save herd changes',
                                type: 'submit',
                                isLoading: isPending,
                                disabled: !isDirty,
                            }}
                            secondaryActions={[
                                {
                                    startIcon: 'delete',
                                    text: 'Delete',
                                    theme: 'danger',
                                    onClick: () => {
                                        routeActions.remove(herdId, { type: 'Herd' });
                                    },
                                },
                            ]}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
